import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  Building2,
  Loader2,
  Calendar,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  ArrowRight,
  CircleDollarSign,
  Receipt,
  CalendarClock,
  Lightbulb,
  Check,
  X,
} from "lucide-react";
import { formatChf } from "../shared/onboarding-types";

interface AccrualSuggestion {
  id: string;
  type: "revenue" | "expense";
  category: "prepaid" | "accrued" | "deferred" | "unbilled";
  description: string;
  amount: number;
  period: string;
  relatedTransactions: number[];
  confidence: number;
  status: "pending" | "approved" | "rejected";
  reasoning: string;
}

interface AccrualSummary {
  prepaidExpenses: number;
  accruedExpenses: number;
  deferredRevenue: number;
  unbilledRevenue: number;
}

export default function AccrualDetector() {
  const { selectedCompanyId } = useCompany();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState("suggestions");

  // Query for company data
  const { data: company, isLoading: isLoadingCompany } =
    trpc.company.get.useQuery(
      { id: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  // Mock accrual suggestions - in production this would come from the backend
  const [suggestions, setSuggestions] = useState<AccrualSuggestion[]>([
    {
      id: "1",
      type: "expense",
      category: "prepaid",
      description: "Annual insurance premium paid in advance",
      amount: 1200000, // CHF 12,000
      period: "Q1 2024",
      relatedTransactions: [101, 102],
      confidence: 92,
      status: "pending",
      reasoning:
        "Payment of CHF 12,000 on Jan 15 for 12-month policy. Monthly expense should be CHF 1,000.",
    },
    {
      id: "2",
      type: "expense",
      category: "accrued",
      description: "Estimated utility costs for December",
      amount: 85000, // CHF 850
      period: "Q4 2024",
      relatedTransactions: [],
      confidence: 78,
      status: "pending",
      reasoning:
        "Based on average monthly utility costs of CHF 850. December invoice not yet received.",
    },
    {
      id: "3",
      type: "revenue",
      category: "deferred",
      description: "Annual software subscription received in advance",
      amount: 3600000, // CHF 36,000
      period: "Q1 2024",
      relatedTransactions: [205],
      confidence: 95,
      status: "pending",
      reasoning:
        "Customer paid CHF 36,000 for 12-month subscription starting Feb 1. Monthly revenue should be CHF 3,000.",
    },
    {
      id: "4",
      type: "revenue",
      category: "unbilled",
      description: "Consulting services delivered but not invoiced",
      amount: 450000, // CHF 4,500
      period: "Q4 2024",
      relatedTransactions: [],
      confidence: 85,
      status: "pending",
      reasoning:
        "Based on timesheet entries for December showing 15 hours at CHF 300/hour.",
    },
    {
      id: "5",
      type: "expense",
      category: "prepaid",
      description: "Office rent paid in advance for Q1 2025",
      amount: 750000, // CHF 7,500
      period: "Q4 2024",
      relatedTransactions: [312],
      confidence: 98,
      status: "approved",
      reasoning: "Rent payment of CHF 7,500 on Dec 15 for Jan-Mar 2025.",
    },
  ]);

  const summary: AccrualSummary = {
    prepaidExpenses: suggestions
      .filter(s => s.type === "expense" && s.category === "prepaid")
      .reduce((sum, s) => sum + s.amount, 0),
    accruedExpenses: suggestions
      .filter(s => s.type === "expense" && s.category === "accrued")
      .reduce((sum, s) => sum + s.amount, 0),
    deferredRevenue: suggestions
      .filter(s => s.type === "revenue" && s.category === "deferred")
      .reduce((sum, s) => sum + s.amount, 0),
    unbilledRevenue: suggestions
      .filter(s => s.type === "revenue" && s.category === "unbilled")
      .reduce((sum, s) => sum + s.amount, 0),
  };

  const pendingCount = suggestions.filter(s => s.status === "pending").length;
  const approvedCount = suggestions.filter(s => s.status === "approved").length;

  const handleApprove = (id: string) => {
    setSuggestions(prev =>
      prev.map(s => (s.id === id ? { ...s, status: "approved" as const } : s))
    );
  };

  const handleReject = (id: string) => {
    setSuggestions(prev =>
      prev.map(s => (s.id === id ? { ...s, status: "rejected" as const } : s))
    );
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    // Simulate analysis
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsAnalyzing(false);
  };

  const getCategoryIcon = (category: AccrualSuggestion["category"]) => {
    switch (category) {
      case "prepaid":
        return <Calendar className="h-4 w-4" />;
      case "accrued":
        return <Clock className="h-4 w-4" />;
      case "deferred":
        return <CalendarClock className="h-4 w-4" />;
      case "unbilled":
        return <Receipt className="h-4 w-4" />;
    }
  };

  const getCategoryLabel = (category: AccrualSuggestion["category"]) => {
    switch (category) {
      case "prepaid":
        return "Prepaid";
      case "accrued":
        return "Accrued";
      case "deferred":
        return "Deferred";
      case "unbilled":
        return "Unbilled";
    }
  };

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">No Company Selected</h2>
            <p className="text-muted-foreground">
              Please select a company to detect accruals.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoadingCompany) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <CalendarClock className="h-8 w-8 text-primary" />
                Accrual Detector
              </h1>
              <p className="text-muted-foreground mt-2">
                AI-powered detection of accruals and deferrals for period-end
                adjustments
              </p>
            </div>
            <Button onClick={handleAnalyze} disabled={isAnalyzing}>
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Run Analysis
                </>
              )}
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-info/20 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-info" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Prepaid Expenses
                    </p>
                    <p className="text-xl font-bold">
                      {formatChf(summary.prepaidExpenses / 100)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-warning/20 rounded-lg">
                    <Clock className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Accrued Expenses
                    </p>
                    <p className="text-xl font-bold">
                      {formatChf(summary.accruedExpenses / 100)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <CalendarClock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Deferred Revenue
                    </p>
                    <p className="text-xl font-bold">
                      {formatChf(summary.deferredRevenue / 100)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-success/20 rounded-lg">
                    <Receipt className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Unbilled Revenue
                    </p>
                    <p className="text-xl font-bold">
                      {formatChf(summary.unbilledRevenue / 100)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger
                value="suggestions"
                className="flex items-center gap-2"
              >
                <Lightbulb className="h-4 w-4" />
                Suggestions
                {pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="approved" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Approved
                <Badge variant="secondary" className="ml-1">
                  {approvedCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="guide" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Guide
              </TabsTrigger>
            </TabsList>

            <TabsContent value="suggestions" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Accrual Suggestions</CardTitle>
                  <CardDescription>
                    Review and approve AI-detected accruals and deferrals
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {suggestions
                      .filter(s => s.status === "pending")
                      .map(suggestion => (
                        <div
                          key={suggestion.id}
                          className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge
                                  variant={
                                    suggestion.type === "revenue"
                                      ? "default"
                                      : "secondary"
                                  }
                                  className="flex items-center gap-1"
                                >
                                  {suggestion.type === "revenue" ? (
                                    <TrendingUp className="h-3 w-3" />
                                  ) : (
                                    <TrendingDown className="h-3 w-3" />
                                  )}
                                  {suggestion.type === "revenue"
                                    ? "Revenue"
                                    : "Expense"}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="flex items-center gap-1"
                                >
                                  {getCategoryIcon(suggestion.category)}
                                  {getCategoryLabel(suggestion.category)}
                                </Badge>
                                <Badge variant="outline">
                                  {suggestion.period}
                                </Badge>
                              </div>
                              <h4 className="font-medium">
                                {suggestion.description}
                              </h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {suggestion.reasoning}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <div className="text-lg font-bold">
                                  {formatChf(suggestion.amount / 100)}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    Confidence:
                                  </span>
                                  <Progress
                                    value={suggestion.confidence}
                                    className="w-20 h-2"
                                  />
                                  <span className="text-sm font-medium">
                                    {suggestion.confidence}%
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReject(suggestion.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(suggestion.id)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}

                    {suggestions.filter(s => s.status === "pending").length ===
                      0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
                        <p>All suggestions have been reviewed!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="approved" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Approved Accruals</CardTitle>
                  <CardDescription>
                    These accruals will be included in period-end journal
                    entries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {suggestions
                      .filter(s => s.status === "approved")
                      .map(suggestion => (
                        <div
                          key={suggestion.id}
                          className="p-4 border rounded-lg bg-success/10 border-success/30"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="h-4 w-4 text-success" />
                                <Badge
                                  variant={
                                    suggestion.type === "revenue"
                                      ? "default"
                                      : "secondary"
                                  }
                                >
                                  {suggestion.type === "revenue"
                                    ? "Revenue"
                                    : "Expense"}
                                </Badge>
                                <Badge variant="outline">
                                  {getCategoryLabel(suggestion.category)}
                                </Badge>
                              </div>
                              <h4 className="font-medium">
                                {suggestion.description}
                              </h4>
                              <div className="text-lg font-bold mt-2">
                                {formatChf(suggestion.amount / 100)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                    {suggestions.filter(s => s.status === "approved").length ===
                      0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                        <p>
                          No approved accruals yet. Review the suggestions tab.
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="guide" className="mt-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingDown className="h-5 w-5" />
                      Expense Accruals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-medium mb-2">
                        Prepaid Expenses (Transitorische Aktiven)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Payments made for services not yet received. Example:
                        Insurance premiums paid in advance.
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Journal Entry:</span>
                        <div className="mt-1 pl-4 border-l-2 border-primary">
                          <div>Dr. Prepaid Expenses (1300)</div>
                          <div>Cr. Expense Account (6xxx)</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-medium mb-2">
                        Accrued Expenses (Passive Rechnungsabgrenzung)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Expenses incurred but not yet paid. Example: Utilities
                        for December, invoice arrives in January.
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Journal Entry:</span>
                        <div className="mt-1 pl-4 border-l-2 border-primary">
                          <div>Dr. Expense Account (6xxx)</div>
                          <div>Cr. Accrued Liabilities (2300)</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Revenue Accruals
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-medium mb-2">
                        Deferred Revenue (Passive Rechnungsabgrenzung)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Payments received for services not yet delivered.
                        Example: Annual subscription paid upfront.
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Journal Entry:</span>
                        <div className="mt-1 pl-4 border-l-2 border-primary">
                          <div>Dr. Revenue Account (3xxx)</div>
                          <div>Cr. Deferred Revenue (2400)</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <h4 className="font-medium mb-2">
                        Unbilled Revenue (Aktive Rechnungsabgrenzung)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Services delivered but not yet invoiced. Example:
                        Consulting hours worked in December.
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Journal Entry:</span>
                        <div className="mt-1 pl-4 border-l-2 border-primary">
                          <div>Dr. Unbilled Revenue (1500)</div>
                          <div>Cr. Revenue Account (3xxx)</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CircleDollarSign className="h-5 w-5" />
                      Swiss Accounting Standards
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium mb-2">
                          Obligationenrecht (OR)
                        </h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>Art. 958 - Proper accrual accounting required</li>
                          <li>Art. 959 - Assets and liabilities valuation</li>
                          <li>Art. 960 - Provisions and accruals treatment</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Swiss GAAP FER</h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>FER 3 - Presentation of financial statements</li>
                          <li>FER 23 - Provisions requirements</li>
                          <li>Matching principle for revenues and expenses</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
}
